'use client';

import React, { useEffect, useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormWrapper } from '../FormWrapper';
import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { Icon } from '@/src/components/ui/Icon';
import { useToast } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import { useAuthStore } from '../../store/auth-store';
import { EHeardFrom } from 'types/src/types';
import { checkUniqueLinkAvailability, signup } from '@/src/actions/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { useRouter } from 'next/navigation';
import { useProfile } from '@/src/hooks/use-profile';
import { editProfile } from '@/src/actions/profile';
import PageLoader from '@/src/components/ui/PageLoader';
import { identifyUser, trackSignup } from '@/src/lib/analytics/posthog';

const HEARD_FROM_OPTIONS = [
  {
    label: 'Player 3 Partner',
    value: EHeardFrom.PARTNER,
    icon: 'logo-heart-3',
  },
  {
    label: 'Friend or Colleague',
    value: EHeardFrom.FRIEND_OR_COLLEAGUE,
    icon: 'user',
  },
  { label: 'YouTube', value: EHeardFrom.YOUTUBE, icon: 'youtube' },
  { label: 'TikTok', value: EHeardFrom.TIK_TOK, icon: 'tiktok' },
  { label: 'Instagram or Facebook', value: EHeardFrom.META, icon: 'meta' },
  { label: 'Bluesky', value: EHeardFrom.BLUESKY, icon: 'bluesky-blue' },
  { label: 'Twitter', value: EHeardFrom.TWITTER, icon: 'twitter-black' },
  { label: 'Google Search', value: EHeardFrom.GOOGLE, icon: 'google' },
  { label: 'Other', value: EHeardFrom.OTHER, icon: '' },
] as const;

const schema = z
  .object({
    heardFrom: z.nativeEnum(EHeardFrom),
    otherSource: z.string().trim().optional(),
    referralCode: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.heardFrom === EHeardFrom.OTHER && !data.otherSource) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherSource'],
        message: 'Please specify the source',
      });
    }
    if (data.heardFrom === EHeardFrom.PARTNER && !data.referralCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['referralCode'],
        message: 'Referral code is required',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export const HeardFromStep: React.FC = () => {
  const router = useRouter();
  const { data, setData } = useAuthStore();
  const toast = useToast();
  const isLoading = useBoolean(false);
  const isDialog = useBoolean(false);
  const isInvalidReferralDlg = useBoolean(false);
  const isReferralError = useBoolean(false);
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);
  const [postDialogRedirectTo, setPostDialogRedirectTo] = useState<
    string | null
  >(null);
  const { data: profile } = useProfile();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      heardFrom: EHeardFrom.PARTNER,
      otherSource: data.otherSource ?? '',
      referralCode: data.referralCode ?? '',
    },
    mode: 'onChange',
  });

  const heardFrom = watch('heardFrom');
  const otherSource = watch('otherSource');
  const referralCode = watch('referralCode');

  useEffect(() => {
    setData({
      heardFrom,
      otherSource: heardFrom === EHeardFrom.OTHER ? otherSource : undefined,
      referralCode: heardFrom === EHeardFrom.PARTNER ? referralCode : undefined,
    });
  }, [heardFrom, otherSource, referralCode, setData]);

  useEffect(() => {
    if (heardFrom !== EHeardFrom.OTHER && otherSource)
      setValue('otherSource', '');
    if (heardFrom !== EHeardFrom.PARTNER && referralCode)
      setValue('referralCode', '');
  }, [heardFrom]);

  const onSubmit = async (values: FormValues) => {
    const source =
      values.heardFrom === EHeardFrom.OTHER ? otherSource : values.heardFrom;

    if (profile) {
      isLoading.onTrue();

      const { data: updatedProfile, error } = await editProfile({
        heardFrom: source,
        referralCode: values.referralCode,
      });

      if (updatedProfile) {
        if (updatedProfile.referralError === 'invalid-code') {
          isInvalidReferralDlg.onTrue();
          setPostDialogRedirectTo(`/${updatedProfile.uniqueLink}`);
          isLoading.onFalse();
          return;
        }
        router.push(`/${updatedProfile.uniqueLink}`);
        isLoading.onFalse();
        return;
      }

      isLoading.onFalse();
    }

    const link = (data.uniqueLink ?? '').trim().toLowerCase();
    if (!link) {
      toast.tip('Unique link is missing. Please go back and set it.');
      return;
    }

    isLoading.onTrue();
    try {
      const isTaken = await checkUniqueLinkAvailability(link);
      if (isTaken) {
        toast.error(
          `The link "${link}" is already taken, please choose another one`,
        );
        return;
      }

      const { firstName, lastName, email, password, countryCode, roleId } =
        data;

      const payload = {
        email: email ?? '',
        password: password ?? '',
        countryCode: countryCode ?? '',
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        uniqueLink: link,
        fullName: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
        role: roleId,
        heardFrom: source,
        referralCode:
          values.heardFrom === EHeardFrom.PARTNER
            ? (values.referralCode ?? '')
            : undefined,
      };

      const {
        data: emailForConfirm,
        referralError,
        error,
        userId,
      } = await signup(payload);

      if (error || !emailForConfirm) {
        toast.error(error ?? 'Sign up failed. Please try again.');
        return;
      }
      identifyUser({ id: userId, email: emailForConfirm });
      trackSignup({
        heardFrom: source,
        role: payload.role,
        countryCode: payload.countryCode,
        referralUsed: Boolean(payload.referralCode),
      });

      referralError ? isReferralError.onTrue() : isReferralError.onFalse();
      isDialog.onTrue();

      setConfirmEmail(emailForConfirm);
      setPostDialogRedirectTo('/sign-in');
      setData({});
    } catch (e: any) {
      toast.error(e?.message ?? 'Unexpected error');
    } finally {
      isLoading.onFalse();
      reset();
    }
  };

  return (
    <>
      {isLoading.value && <PageLoader />}
      <FormWrapper title={['Where did you hear', 'about us?']}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full md:px-10 xl:px-15"
        >
          <div className="flex gap-2 flex-wrap">
            {HEARD_FROM_OPTIONS.map((option) => {
              const selected = heardFrom === option.value;
              return (
                <label
                  key={option.value}
                  className={`relative flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200
                    ${selected ? 'border-blue-primary' : 'border-gray-100 bg-white hover:border-gray-300 hover:bg-bg-gray'}`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    {...register('heardFrom')}
                    className="sr-only"
                  />

                  <div className="flex-shrink-0">
                    {option.icon !== '' && (
                      <Icon name={option.icon} className="size-5" />
                    )}
                  </div>

                  <span className="text-black/80 font-normal text-xs md:text-sm">
                    {option.label}
                  </span>
                  <div
                    className={`flex-shrink-0 size-4 rounded-full border-1 transition-all duration-200
                    ${selected ? 'border-blue-primary bg-white' : 'border-gray-300 bg-white'}`}
                  >
                    {selected && (
                      <div className="w-full h-full rounded-full bg-blue-primary scale-50" />
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {errors.heardFrom && (
            <p className="text-red-error text-sm mt-2">
              {errors.heardFrom.message as string}
            </p>
          )}

          {heardFrom === EHeardFrom.OTHER && (
            <div className="mt-6">
              <Input
                id="otherSource"
                placeholder="Enter the source"
                {...register('otherSource')}
                className="h-12"
              />
              {errors.otherSource && (
                <p className="text-red-error text-sm mt-1">
                  {errors.otherSource.message as string}
                </p>
              )}
            </div>
          )}

          {heardFrom === EHeardFrom.PARTNER && (
            <div className="mt-6">
              <Input
                id="referralCode"
                placeholder="Enter your referral code"
                {...register('referralCode')}
                className="h-12"
              />
              {errors.referralCode && (
                <p className="text-red-error text-sm mt-1">
                  {errors.referralCode.message as string}
                </p>
              )}
            </div>
          )}

          <Button
            className="w-full md:mt-8 mt-5 md:h-[51px] h-10"
            disabled={!isValid || isLoading.value}
          >
            Press Start
          </Button>
        </form>
      </FormWrapper>

      <Dialog
        open={isDialog.value}
        onOpenChange={(open) => !open && isDialog.onFalse()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle></DialogTitle>
            <DialogDescription>
              Please check <b>{confirmEmail}</b> for a verification email and
              follow the link to continue.
              {isReferralError.value && (
                <span className="text-red-error block mt-2">
                  Referral code is invalid. You can add a valid code later in
                  your profile settings.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                isDialog.onFalse();
                if (postDialogRedirectTo) router.push(postDialogRedirectTo);
              }}
              className="px-4"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isInvalidReferralDlg.value}
        onOpenChange={(open) => {
          if (!open) {
            isInvalidReferralDlg.onFalse();
            if (postDialogRedirectTo) router.push(postDialogRedirectTo);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Referral code is invalid</DialogTitle>
            <DialogDescription>
              You can add a valid code later in your profile settings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                isInvalidReferralDlg.onFalse();
                if (postDialogRedirectTo) router.push(postDialogRedirectTo);
              }}
              className="px-4"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
